import React, { useState, useEffect } from 'react';
import { Upload, X, FileText, Trash2, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import styled from '@emotion/styled';
import { authenticatedFetch, getOrganizationId, getCollectionName } from '../utils/auth';
import { useKeycloak } from '@react-keycloak/web';
import { useTranslation } from 'react-i18next';

// Get the API base URL from environment variable or use a default
// const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://sales-ragent-api.supervity.ai';

// Styled tooltip component
const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  &:hover .tooltip-content {
    visibility: visible;
    opacity: 1;
  }
`;

const TooltipContent = styled.div`
  visibility: hidden;
  position: absolute;
  right: calc(100% + 10px);
  top: -5px;
  background-color: #333;
  color: white;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  width: 280px;
  z-index: 50;
  opacity: 0;
  transition: opacity 0.2s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  line-height: 1.5;

  /* Arrow */
  &:before {
    content: "";
    position: absolute;
    right: -6px;
    top: 10px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent transparent #333;
  }
`;

const DocumentManager = ({ companyName, dashboardId }) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const { keycloak, initialized } = useKeycloak();

  const documentTags = [
    'basic_information',
    'vision_analysis',
    'management_strategy',
    'management_message',
    'crisis_management',
    'dx_analysis',
    'financial_analysis',
    'competitive_landscape',
    'regulatory_environment',
    'business_structure',
    'account_research'
  ];

  // Upload instructions text
  const uploadInstructions = `
    Valid file types: *.pptx, *.docx, *.xlsx, *.pdf, *.mp3, *.mp4, *.wav, *.m4a, *.mpeg, *.flac, *.eml, *.msg

    File size limits:
    • Documents (PDF, PPTX, DOCX): Max 50MB
    • Audio/Video Files: Max 24MB
    • Spreadsheets (XLSX): Max 10MB

    Document name limit:
    • Maximum 64 characters (including file extension)
  `;

  // Helper function to get display name for tags
  const getTagDisplayName = (tag) => {
    if (tag === 'account_research') {
      return '3 yr account strategy plan';
    }
    return tag.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Fetch documents on component mount and when dashboardId changes
  useEffect(() => {
    if (dashboardId) {
      fetchDocuments();
    }
  }, [dashboardId]);


  const fetchDocuments = async () => {
    if (!dashboardId) return;
    setIsLoading(true);
    try {
      const response = await authenticatedFetch(`/api/dashboards/${dashboardId}/documents`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to fetch documents');
      }

      const data = await response.json();
      // Backend returns array of DocumentResponse objects
      setDocuments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error(error.message || t('common.fetchDocumentsError'));
    } finally {
      setIsLoading(false);
    }
  };


  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  const removeTag = (tagToRemove) => {
    setSelectedTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if at least one tag is selected
    if (selectedTags.length === 0) {
      toast.error(t('common.selectTagBeforeUpload'));
      event.target.value = '';
      return;
    }

    // Check file type
    const validExtensions = [
      '.pptx', '.docx', '.xlsx', '.pdf', '.mp3', '.mp4', 
      '.wav', '.m4a', '.mpeg', '.flac', '.eml', '.msg'
    ];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      toast.error(t('common.invalidFileType'));
      event.target.value = '';
      return;
    }

    // Check file size
    const maxSizes = {
      document: 64 * 1024 * 1024, // 64MB for documents
      media: 24 * 1024 * 1024,    // 24MB for audio/video
      spreadsheet: 10 * 1024 * 1024 // 10MB for spreadsheets
    };

    const documentTypes = ['.pdf', '.pptx', '.docx'];
    const mediaTypes = ['.mp3', '.mp4', '.wav', '.m4a', '.mpeg', '.flac'];
    const spreadsheetTypes = ['.xlsx'];

    let maxSize = maxSizes.document;
    if (mediaTypes.includes(fileExtension)) maxSize = maxSizes.media;
    if (spreadsheetTypes.includes(fileExtension)) maxSize = maxSizes.spreadsheet;

    if (file.size > maxSize) {
      const sizeMB = Math.round(maxSize / (1024 * 1024));
      toast.error(t('common.fileTooLarge', { size: sizeMB }));
      event.target.value = '';
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('company_name', companyName);
    formData.append('dashboard_id', dashboardId);
    formData.append('document_name', JSON.stringify(selectedTags));
    console.log("Selected tags:", selectedTags);
    console.log('Uploading document with company name:', companyName);
    console.log('Uploading document with dashboard ID:', dashboardId);
    try {
      const response = await authenticatedFetch(`/api/upload_document`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to upload document');
      }

      // Parse the response to check for nested errors
      const responseData = await response.json();
      
      // Check if there's an exception in the upload_result
      if (responseData.upload_result && responseData.upload_result.status === 'EXCEPTION') {
        throw new Error(responseData.upload_result.message || 'Upload failed');
      }
      
      // Check if the overall status indicates success
      if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Upload failed');
      }

      toast.success(t('common.documentUploaded'));
      fetchDocuments(); // Refresh the documents list
      setSelectedTags([]); // Reset the selected tags
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.message || t('common.uploadError'));
    } finally {
      setIsUploading(false);
      event.target.value = ''; // Reset file input
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm(t('common.deleteDocumentConfirm'))) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await authenticatedFetch(
        `/api/dashboards/${dashboardId}/documents/${documentId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete document');
      }

      toast.success(t('common.documentDeleted'));
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(error.message || t('common.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-medium">Documents</h3>
        <TooltipContainer>
          <Info size={16} className="text-gray-400 cursor-help" />
          <TooltipContent className="tooltip-content whitespace-pre-line">
            {uploadInstructions}
          </TooltipContent>
        </TooltipContainer>
      </div>

      <div className="p-4">
        {/* Upload Section */}
        <div className="mb-4 space-y-4">
          {/* Document Tags Multiselect */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Tags (Select multiple)
            </label>
            
            {/* Selected Tags Display */}
            {selectedTags.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {selectedTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {getTagDisplayName(tag)}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 hover:text-blue-600"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Tags Grid */}
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-md">
              {documentTags.map((tag) => (
                <label
                  key={tag}
                  className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(tag)}
                    onChange={() => handleTagToggle(tag)}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm">
                    {getTagDisplayName(tag)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Upload Button */}
          <div>
            <label 
              htmlFor="file-upload" 
              className={`cursor-pointer flex items-center justify-center w-full px-4 py-2 border border-gray-300 border-dashed rounded-md hover:bg-gray-50 ${
                selectedTags.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Upload size={16} className="mr-2 text-gray-500" />
              <span className="text-sm text-gray-600">
                {isUploading ? 'Uploading...' : 'Upload Document'}
              </span>
              <input 
                id="file-upload" 
                type="file" 
                className="hidden"
                accept=".pptx,.docx,.xlsx,.pdf,.mp3,.mp4,.wav,.m4a,.mpeg,.flac,.eml,.msg"
                onChange={handleFileUpload}
                disabled={isUploading || selectedTags.length === 0}
              />
            </label>
            {selectedTags.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">Please select at least one tag before uploading</p>
            )}
          </div>
        </div>

        {/* Documents List */}
        <div className="max-h-[calc(100vh-250px)] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center my-8">
              <div className="w-8 h-8 border-4 border-t-blue-500 border-blue-200 rounded-full animate-spin"></div>
            </div>
          ) : documents.length > 0 ? (
            <ul className="space-y-2">
              {documents.map((doc, index) => (
                <li key={index} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded">
                  <div className="flex items-center flex-1 min-w-0 mr-2">
                    <FileText size={14} className="mr-2 flex-shrink-0 text-gray-500" />
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm truncate font-medium">
                        {doc.file_name}
                      </span>
                      {doc.category && (
                        <span className="text-[10px] text-blue-500 uppercase tracking-wider">
                          {getTagDisplayName(doc.category)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => handleDeleteDocument(doc.id)}
                    className="text-gray-400 hover:text-red-500 flex-shrink-0"
                    disabled={isDeleting}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No documents uploaded yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentManager; 